import React, { useState } from 'react'
import './StateBanner.css'

const VALID_VARIANTS = new Set(['ai', 'soft', 'info', 'urgent'])

function getDefaultSize(variant, size) {
  if (size) return size
  return variant === 'ai' ? 'inline' : 'banner'
}

function cx(...parts) {
  return parts.filter(Boolean).join(' ')
}

function StateBannerAction({ variant, action, secondary = false }) {
  if (!action) return null

  return (
    <button
      type="button"
      className={cx(
        'state-banner__action',
        `state-banner__action--${variant}`,
        secondary && 'state-banner__action--secondary',
      )}
      onClick={action.onClick}
    >
      {action.label}
    </button>
  )
}

export default function StateBanner({
  variant,
  size,
  eyebrow,
  headline,
  body,
  action,
  secondaryAction,
  dismissible = false,
  onDismiss,
  className = '',
  ...rest
}) {
  const [dismissed, setDismissed] = useState(false)

  if (!VALID_VARIANTS.has(variant)) {
    throw new Error(`StateBanner variant must be one of: ${Array.from(VALID_VARIANTS).join(', ')}`)
  }

  const canDismiss = Boolean(dismissible)
  const resolvedSize = getDefaultSize(variant, size)
  const showIcon = variant === 'ai'

  if (dismissed) return null

  function handleDismiss() {
    setDismissed(true)
    onDismiss?.()
  }

  return (
    <aside
      {...rest}
      className={cx(
        'state-banner',
        `state-banner--${variant}`,
        `state-banner--${resolvedSize}`,
        (action || secondaryAction) && 'state-banner--with-action',
        canDismiss && 'state-banner--dismissible',
        className
      )}
    >
      {showIcon && (
        <span className="state-banner__icon" aria-hidden="true">
          ✦
        </span>
      )}

      <div className="state-banner__content">
        {eyebrow && <div className="state-banner__eyebrow">{eyebrow}</div>}
        {headline && <div className="state-banner__headline">{headline}</div>}
        <div className="state-banner__body">{body}</div>
      </div>

      {(action || secondaryAction) && (
        <div className="state-banner__actions">
          <StateBannerAction variant={variant} action={action} />
          <StateBannerAction variant={variant} action={secondaryAction} secondary />
        </div>
      )}

      {canDismiss && (
        <button
          type="button"
          className="state-banner__dismiss"
          aria-label="Dismiss message"
          onClick={handleDismiss}
        >
          ×
        </button>
      )}
    </aside>
  )
}
